/**
 * The warehouse form's field block, shared by two hosts (#268): the full-page
 * composer at `/inventory/warehouses/new|edit`, and the store product form's
 * inline `WarehouseCreateDialog` — the "No warehouses yet" dead-end needed a
 * create path that doesn't leave the form.
 *
 * Styled with `@flint/ui` primitives + tokens rather than the composer's
 * `wc-*` classes: those are all scoped under `.warehouse-composer-page`, so
 * inside a dialog they simply don't apply and the fields rendered bare. The
 * primitives look right in both hosts and track the design system for free.
 *
 * Deliberately dumb: values + onPatch come from the host, which owns
 * submission, error display and navigation. `isActive` renders only in edit
 * mode — a warehouse is born active.
 */
import {
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@flint/ui";

export interface WarehouseFormValues {
  name: string;
  code: string;
  description: string;
  warehouseType: "physical" | "virtual" | "job_work";
  isActive: boolean;
  isDefault: boolean;
}

export const EMPTY_WAREHOUSE_FORM: WarehouseFormValues = {
  name: "",
  code: "",
  description: "",
  warehouseType: "physical",
  isActive: true,
  isDefault: false,
};

const LABEL = "text-sm font-medium leading-[18px] text-ink-2";
const HELP = "text-xs text-muted";

export function WarehouseFormFields({
  values,
  onPatch,
  mode,
}: {
  values: WarehouseFormValues;
  onPatch: <K extends keyof WarehouseFormValues>(key: K, value: WarehouseFormValues[K]) => void;
  mode: "create" | "edit";
}) {
  const isEdit = mode === "edit";
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {/* Name */}
      <div className="flex flex-col gap-1.5 sm:col-span-2">
        <label htmlFor="wh-name" className={LABEL}>
          Name *
        </label>
        <Input
          id="wh-name"
          type="text"
          placeholder="e.g. Main Godown — Tirupur"
          value={values.name}
          onChange={(e) => onPatch("name", e.target.value)}
          required
          autoFocus
        />
        <span className={HELP}>Shown in stock movement pickers and balance reports.</span>
      </div>

      {/* Code */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="wh-code" className={LABEL}>
          Code <span className="font-normal text-muted">(optional)</span>
        </label>
        <Input
          id="wh-code"
          type="text"
          placeholder="e.g. RMS"
          value={values.code}
          maxLength={20}
          onChange={(e) => onPatch("code", e.target.value)}
        />
        <span className={HELP}>Short identifier shown in stock pickers and reports.</span>
      </div>

      {/* Type */}
      <div className="flex flex-col gap-1.5">
        <span className={LABEL}>Type</span>
        <Select
          value={values.warehouseType}
          onValueChange={(v) => onPatch("warehouseType", v as WarehouseFormValues["warehouseType"])}
        >
          <SelectTrigger className="!h-9 !text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="physical">Physical</SelectItem>
            <SelectItem value="virtual">Virtual</SelectItem>
            <SelectItem value="job_work">Job Work</SelectItem>
          </SelectContent>
        </Select>
        <span className={HELP}>
          Physical = godown/store · Virtual = transit/consignment · Job Work = vendor processing
        </span>
      </div>

      {/* Default warehouse */}
      <div className="flex flex-col gap-1.5 sm:col-span-2">
        <Checkbox
          label="Use as company default"
          checked={values.isDefault}
          onCheckedChange={(next) => onPatch("isDefault", next === true)}
        />
        <span className={HELP}>
          When a GRN or invoice has no warehouse selected, stock moves here automatically. Only one
          warehouse can be the default.
        </span>
      </div>

      {/* Active toggle — edit mode only */}
      {isEdit ? (
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <span className={LABEL}>Status</span>
          <div className="flex items-center gap-4 text-sm text-ink-2">
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name="wh-active"
                checked={values.isActive}
                onChange={() => onPatch("isActive", true)}
              />
              Active
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name="wh-active"
                checked={!values.isActive}
                onChange={() => onPatch("isActive", false)}
              />
              Inactive
            </label>
          </div>
          <span className={HELP}>
            Inactive warehouses are hidden by default in stock movement pickers.
          </span>
        </div>
      ) : null}

      {/* Description */}
      <div className="flex flex-col gap-1.5 sm:col-span-2">
        <label htmlFor="wh-description" className={LABEL}>
          Description <span className="font-normal text-muted">(optional)</span>
        </label>
        <Textarea
          id="wh-description"
          placeholder="e.g. Ground floor, Bay 3 — raw materials only"
          value={values.description}
          maxLength={300}
          rows={3}
          onChange={(e) => onPatch("description", e.target.value)}
        />
        <span className={HELP}>Internal note. Not shown on customer documents.</span>
      </div>
    </div>
  );
}
